/**
 * Standing-phone stow: a 9-pocket album the pulls fly into. The attaché case
 * is too wide for a portrait canvas, so this page-on-easel pose stays on
 * screen. Desktop and landscape still use CollectionVault.
 */

import * as THREE from 'three'
import { CARD_BACK_URL } from '../../utils/cardImages'
import { loadCardImage } from '../../utils/cardImageCache'
import {
  applyWebglBudget,
  listenWebglLost,
  webglRendererOptions,
} from '../webglPhone'
import { fitFov, packFrame, type PackFrame } from './packFrame'
import './CollectionVault.css'

import type { VaultCallbacks } from './CollectionVault'

interface FlyCard {
  group: THREE.Group
  delay: number
  duration: number
  start: THREE.Vector3
  mid: THREE.Vector3
  end: THREE.Vector3
  startEuler: THREE.Euler
  endEuler: THREE.Euler
  startScale: number
  endScale: number
  landed: boolean
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t
const clamp01 = (t: number): number => Math.max(0, Math.min(1, t))
const easeOut = (t: number): number => 1 - (1 - t) ** 3
const easeInOut = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2

const bezier = (a: number, b: number, c: number, t: number): number => {
  const u = 1 - t
  return u * u * a + 2 * u * t * b + t * t * c
}

const CARD_W = 0.82
const CARD_H = 1.2
const PAGE_W = 1.28
const PAGE_H = 1.76
const COVER_T = 0.048

export class CollectionBinder {
  readonly kind = 'binder' as const
  private readonly root: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.PerspectiveCamera
  private readonly cover: THREE.Group
  private readonly page: THREE.Group
  private readonly disposables: { dispose: () => void }[] = []
  private readonly cards: FlyCard[] = []
  private readonly callbacks: VaultCallbacks
  private raf = 0
  private unlistenGl: (() => void) | null = null
  private contextLost = false
  private startedAt = 0
  private skipped = false
  private disposed = false
  private finished = false
  private lidClosedPlayed = false
  private texturesReady: Promise<void>
  private resolvePlay: (() => void) | null = null
  private readonly resizeObs: ResizeObserver
  private frame: PackFrame = 'portrait'
  private readonly coverOpenAt = 0.1
  private readonly coverOpenDur = 0.7
  private readonly cardsStart = 0.72
  private readonly flyDur = 0.78
  private readonly stagger: number
  private readonly lidCloseAt: number
  private readonly completeAt: number

  /**
   * Mount a binder stow on `host` and load `cardUrls` onto the page.
   */
  constructor(
    host: HTMLElement,
    cardUrls: string[],
    callbacks: VaultCallbacks = {},
    labelText = 'Collection',
  ) {
    this.callbacks = callbacks
    const n = Math.max(1, cardUrls.length)
    this.stagger = Math.min(0.1, 1.8 / n)
    const lastStart = this.cardsStart + (n - 1) * this.stagger
    this.lidCloseAt = lastStart + this.flyDur + 0.18
    this.completeAt = lastStart + this.flyDur + 0.85

    this.root = document.createElement('div')
    this.root.className = 'cv-root'
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'cv-canvas'
    const label = document.createElement('p')
    label.className = 'cv-label'
    label.textContent = labelText
    this.root.append(this.canvas, label)

    try {
      this.renderer = new THREE.WebGLRenderer(webglRendererOptions(this.canvas))
    } catch (err) {
      this.canvas.remove()
      throw err
    }
    applyWebglBudget(this.renderer, 2)
    this.unlistenGl = listenWebglLost(this.canvas, this.handleContextLost)

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(42, 1, 0.1, 40)

    this.addLights()
    const built = this.buildBinder()
    this.cover = built.cover
    this.page = built.page
    this.resizeObs = new ResizeObserver(() => this.resize())

    try {
      host.appendChild(this.root)
      this.texturesReady = this.buildCards(cardUrls)
      this.resize()
      this.resizeObs.observe(this.root)
    } catch (err) {
      this.unlistenGl()
      this.unlistenGl = null
      this.resizeObs.disconnect()
      this.root.remove()
      throw err
    }
  }

  private handleContextLost = (): void => {
    if (this.disposed || this.contextLost) return
    this.contextLost = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    this.callbacks.onContextLost?.()
  }

  play(): Promise<void> {
    return new Promise((resolve) => {
      if (this.disposed) {
        resolve()
        return
      }
      this.resolvePlay = resolve
      const start = () => {
        if (this.disposed) {
          resolve()
          return
        }
        this.startedAt = performance.now()
        this.tick()
      }
      void this.texturesReady.then(start).catch(start)
    })
  }

  skip(): void {
    if (this.disposed || this.finished) return
    this.skipped = true
  }

  dispose(): void {
    this.disposed = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.unlistenGl?.()
    this.unlistenGl = null
    this.resizeObs.disconnect()
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (mesh.isMesh) {
        mesh.geometry?.dispose()
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material]
        for (const mat of mats) mat?.dispose()
      }
    })
    for (const item of this.disposables) item.dispose()
    this.renderer.dispose()
    this.root.remove()
    this.resolvePlay?.()
    this.resolvePlay = null
  }

  private addLights(): void {
    const ambient = new THREE.AmbientLight(0xd7e3f4, 0.78)
    const key = new THREE.DirectionalLight(0xfff7ea, 1.35)
    key.position.set(1.2, 4.6, 3.8)
    key.castShadow = true
    key.shadow.mapSize.set(1024, 1024)
    const rim = new THREE.DirectionalLight(0x93c5fd, 0.55)
    rim.position.set(-2.4, 2.2, -1.6)
    const fill = new THREE.PointLight(0xffffff, 0.7, 12)
    fill.position.set(0.2, 1.8, 2.8)
    this.scene.add(ambient, key, rim, fill)
  }

  private trackGeo(geo: THREE.BufferGeometry): THREE.BufferGeometry {
    this.disposables.push(geo)
    return geo
  }

  private leather(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0x152238,
      roughness: 0.72,
      metalness: 0.08,
      map: this.makeGrain(0x152238, 0x0b1220),
    })
    this.disposables.push(mat)
    return mat
  }

  private gold(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      roughness: 0.28,
      metalness: 0.86,
    })
    this.disposables.push(mat)
    return mat
  }

  private chrome(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xc5ccd4,
      roughness: 0.22,
      metalness: 0.92,
    })
    this.disposables.push(mat)
    return mat
  }

  private makeGrain(a: number, b: number): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 256
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    ctx.fillStyle = `#${a.toString(16).padStart(6, '0')}`
    ctx.fillRect(0, 0, 256, 256)
    for (let i = 0; i < 2400; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.18})`
      ctx.fillRect(Math.random() * 256, Math.random() * 256, 2, 2)
    }
    ctx.fillStyle = `#${b.toString(16).padStart(6, '0')}22`
    ctx.fillRect(0, 0, 256, 256)
    const tex = new THREE.CanvasTexture(c)
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(2, 2)
    tex.colorSpace = THREE.SRGBColorSpace
    this.disposables.push(tex)
    return tex
  }

  private makeCoverMark(): THREE.CanvasTexture {
    const size = 512
    const c = document.createElement('canvas')
    c.width = c.height = size
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    ctx.fillStyle = '#152238'
    ctx.fillRect(0, 0, size, size)
    ctx.strokeStyle = '#d4af37'
    ctx.lineWidth = 14
    ctx.beginPath()
    ctx.arc(256, 220, 118, 0, Math.PI * 2)
    ctx.stroke()
    ctx.strokeStyle = '#f8e7a0'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.arc(256, 220, 96, 0, Math.PI * 2)
    ctx.stroke()
    ctx.fillStyle = '#f8fafc'
    ctx.font = 'bold 54px Georgia, "Times New Roman", serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('DC', 256, 220)
    ctx.fillStyle = '#d4af37'
    ctx.font = 'bold 28px Arial, sans-serif'
    ctx.fillText('COLLECTION', 256, 390)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    this.disposables.push(tex)
    return tex
  }

  private makePageTex(cols: number, rows: number): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = 1024
    c.height = 1408
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    ctx.fillStyle = '#0b1220'
    ctx.fillRect(0, 0, 1024, 1408)
    ctx.strokeStyle = '#1e3a5f'
    ctx.lineWidth = 18
    ctx.strokeRect(18, 18, 988, 1372)
    const padX = 70
    const padY = 80
    const gap = 22
    const cellW = (1024 - padX * 2 - gap * Math.max(0, cols - 1)) / cols
    const cellH = (1408 - padY * 2 - gap * Math.max(0, rows - 1)) / rows
    for (let r = 0; r < rows; r++) {
      for (let col = 0; col < cols; col++) {
        const x = padX + col * (cellW + gap)
        const y = padY + r * (cellH + gap)
        ctx.fillStyle = '#071018'
        ctx.fillRect(x, y, cellW, cellH)
        ctx.strokeStyle = '#d4af37'
        ctx.lineWidth = 4
        ctx.strokeRect(x + 4, y + 4, cellW - 8, cellH - 8)
        ctx.strokeStyle = '#334155'
        ctx.lineWidth = 2
        ctx.strokeRect(x + 12, y + 12, cellW - 24, cellH - 24)
      }
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    this.disposables.push(tex)
    return tex
  }

  private buildBinder(): {
    binder: THREE.Group
    cover: THREE.Group
    page: THREE.Group
  } {
    const leather = this.leather()
    const gold = this.gold()
    const chrome = this.chrome()
    const binder = new THREE.Group()
    binder.rotation.x = -0.2

    const floor = new THREE.Mesh(
      this.trackGeo(new THREE.CircleGeometry(3.2, 36)),
      new THREE.ShadowMaterial({ opacity: 0.38 }),
    )
    floor.rotation.x = -Math.PI / 2
    floor.position.y = -0.02
    floor.receiveShadow = true
    this.scene.add(floor)

    const back = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(PAGE_W + 0.04, PAGE_H + 0.04, COVER_T)),
      leather,
    )
    back.position.set(0, PAGE_H / 2 + 0.02, -COVER_T)
    back.castShadow = true
    binder.add(back)

    const page = new THREE.Group()
    page.position.set(0, PAGE_H / 2 + 0.02, 0)
    binder.add(page)

    const spine = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(0.08, PAGE_H + 0.05, 0.1)),
      leather,
    )
    spine.position.set(-PAGE_W / 2 - 0.01, PAGE_H / 2 + 0.02, -COVER_T / 2)
    binder.add(spine)

    const ringGeo = this.trackGeo(new THREE.TorusGeometry(0.045, 0.01, 8, 18))
    for (const y of [0.28, PAGE_H / 2 + 0.02, PAGE_H - 0.24]) {
      const ring = new THREE.Mesh(ringGeo, chrome)
      ring.position.set(-PAGE_W / 2 + 0.01, y, 0.01)
      ring.rotation.y = Math.PI / 2
      binder.add(ring)
    }

    const cover = new THREE.Group()
    cover.position.set(-PAGE_W / 2, PAGE_H / 2 + 0.02, COVER_T * 0.6)
    const coverMesh = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(PAGE_W + 0.04, PAGE_H + 0.04, COVER_T)),
      leather,
    )
    coverMesh.position.set(PAGE_W / 2, 0, 0)
    coverMesh.castShadow = true
    const mark = new THREE.Mesh(
      this.trackGeo(new THREE.PlaneGeometry(0.7, 0.7)),
      new THREE.MeshStandardMaterial({
        map: this.makeCoverMark(),
        roughness: 0.45,
        metalness: 0.12,
      }),
    )
    mark.position.set(PAGE_W / 2, 0.08, COVER_T / 2 + 0.001)
    const trim = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(PAGE_W + 0.06, 0.03, COVER_T + 0.01)),
      gold,
    )
    trim.position.set(PAGE_W / 2, PAGE_H / 2 - 0.04, 0)
    cover.add(coverMesh, mark, trim)
    binder.add(cover)

    this.scene.add(binder)
    return { binder, cover, page }
  }

  /**
   * Pocket on the page, origin at the page center, facing +Z.
   */
  private gridSlot(
    n: number,
    i: number,
  ): { pos: THREE.Vector3; x: number; y: number; scale: number } {
    const cols = n <= 3 ? Math.max(1, n) : n <= 9 ? 3 : n <= 12 ? 4 : 5
    const rows = Math.ceil(n / cols)
    const innerW = PAGE_W * 0.86
    const innerH = PAGE_H * 0.86
    const gap = 0.028
    const cellW = (innerW - gap * Math.max(0, cols - 1)) / cols
    const cellH = (innerH - gap * Math.max(0, rows - 1)) / rows
    const scale = Math.min(cellW / CARD_W, cellH / CARD_H) * 0.9
    const col = i % cols
    const row = Math.floor(i / cols)
    const rowCount = row === rows - 1 ? n - row * cols : cols
    const usedW = rowCount * cellW + Math.max(0, rowCount - 1) * gap
    const usedH = rows * cellH + Math.max(0, rows - 1) * gap
    const x = -usedW / 2 + cellW / 2 + col * (cellW + gap)
    const y = usedH / 2 - cellH / 2 - row * (cellH + gap)
    return {
      x,
      y,
      scale,
      pos: new THREE.Vector3(x, y, 0.014),
    }
  }

  private async buildCards(urls: string[]): Promise<void> {
    let backTex: THREE.Texture
    try {
      backTex = await this.loadTex(CARD_BACK_URL)
    } catch {
      backTex = this.makeGrain(0x1e3a5f, 0x0b1220)
    }
    const n = urls.length
    const cols = n <= 3 ? Math.max(1, n) : n <= 9 ? 3 : n <= 12 ? 4 : 5
    const rows = Math.ceil(n / cols)
    const pageMat = new THREE.MeshStandardMaterial({
      map: this.makePageTex(cols, rows),
      roughness: 0.82,
      metalness: 0.04,
      side: THREE.DoubleSide,
    })
    this.disposables.push(pageMat)
    const sheet = new THREE.Mesh(
      this.trackGeo(new THREE.PlaneGeometry(PAGE_W, PAGE_H)),
      pageMat,
    )
    sheet.receiveShadow = true
    this.page.add(sheet)

    const plane = this.trackGeo(new THREE.PlaneGeometry(CARD_W, CARD_H))
    this.cards.length = n

    await Promise.all(
      urls.map(async (url, i) => {
        let frontTex: THREE.Texture
        try {
          frontTex = await this.loadTex(url)
        } catch {
          frontTex = this.makeGrain(0x334155, 0x1e293b)
        }
        const frontMat = new THREE.MeshStandardMaterial({
          map: frontTex,
          roughness: 0.35,
          metalness: 0.05,
          emissive: new THREE.Color(0x222222),
          emissiveMap: frontTex,
        })
        const backMat = new THREE.MeshStandardMaterial({
          map: backTex,
          roughness: 0.45,
        })
        this.disposables.push(frontMat, backMat)
        const group = new THREE.Group()
        const front = new THREE.Mesh(plane, frontMat)
        const back = new THREE.Mesh(plane, backMat)
        back.rotation.y = Math.PI
        front.castShadow = true
        group.add(front, back)
        group.visible = false
        this.page.add(group)

        const fan = n === 1 ? 0 : (i / (n - 1)) * 2 - 1
        const slot = this.gridSlot(n, i)
        this.cards[i] = {
          group,
          delay: this.cardsStart + i * this.stagger,
          duration: this.flyDur,
          start: new THREE.Vector3(fan * 0.55, 0.15, 1.45),
          mid: new THREE.Vector3(slot.x * 0.4, slot.y + 0.28, 0.62),
          end: slot.pos,
          startEuler: new THREE.Euler(-0.18, fan * 0.16, fan * 0.06),
          endEuler: new THREE.Euler(0, 0, 0),
          startScale: 0.92,
          endScale: slot.scale,
          landed: false,
        }
      }),
    )
  }

  private loadTex(url: string): Promise<THREE.Texture> {
    return loadCardImage(url).then((img) => {
      const tex = new THREE.Texture(img)
      tex.colorSpace = THREE.SRGBColorSpace
      tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
      tex.needsUpdate = true
      this.disposables.push(tex)
      return tex
    })
  }

  private refreshFrame(): void {
    this.frame = packFrame(this.root.clientWidth, this.root.clientHeight)
  }

  private placeCamera(settle: number): void {
    const short = this.frame === 'short'
    const radius = lerp(short ? 3.85 : 3.45, short ? 3.15 : 2.72, settle)
    const height = lerp(short ? 1.05 : 1.22, short ? 1.18 : 1.32, settle)
    const lookY = lerp(0.82, 0.9, settle)
    const lookZ = lerp(0.22, 0.12, settle)
    this.camera.position.set(0, height, radius)
    const look = new THREE.Vector3(0.02, lookY, lookZ)
    this.camera.lookAt(look)
    const dist = this.camera.position.distanceTo(look)
    const fitW = short ? PAGE_W + 1.15 : PAGE_W + 0.42
    const fitH = short ? PAGE_H + 0.35 : PAGE_H + 0.55
    this.camera.fov = fitFov(this.camera.aspect, dist, fitW, fitH, 34, 56)
    this.camera.updateProjectionMatrix()
  }

  private resize(): void {
    const w = Math.max(1, this.root.clientWidth)
    const h = Math.max(1, this.root.clientHeight)
    this.refreshFrame()
    this.camera.aspect = w / h
    this.placeCamera(0)
    this.renderer.setSize(w, h, false)
  }

  private tick = (): void => {
    if (this.disposed || this.contextLost) return
    const elapsed = (performance.now() - this.startedAt) / 1000
    const t = this.skipped ? this.completeAt : elapsed

    const openT = easeOut(clamp01((t - this.coverOpenAt) / this.coverOpenDur))
    this.cover.rotation.y = lerp(0, 2.18, openT)

    if (t >= this.lidCloseAt && !this.lidClosedPlayed) {
      this.lidClosedPlayed = true
      this.callbacks.onLidClose?.()
    }

    const settle = easeInOut(
      clamp01(
        (t - this.coverOpenAt) /
          Math.max(0.2, this.completeAt - this.coverOpenAt),
      ),
    )
    this.placeCamera(settle)

    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]
      if (!card) continue
      const local = clamp01((t - card.delay) / card.duration)
      if (t < card.delay - 0.02) {
        card.group.visible = false
        continue
      }
      card.group.visible = true
      const p = easeInOut(local)
      card.group.position.set(
        bezier(card.start.x, card.mid.x, card.end.x, p),
        bezier(card.start.y, card.mid.y, card.end.y, p),
        bezier(card.start.z, card.mid.z, card.end.z, p),
      )
      card.group.rotation.set(
        lerp(card.startEuler.x, card.endEuler.x, p),
        lerp(card.startEuler.y, card.endEuler.y, p),
        lerp(card.startEuler.z, card.endEuler.z, p),
      )
      const s = lerp(card.startScale, card.endScale, p)
      card.group.scale.setScalar(s)
      if (local >= 1 && !card.landed) {
        card.landed = true
        if (!this.skipped) this.callbacks.onCardLand?.(i)
      }
    }

    this.renderer.render(this.scene, this.camera)
    if (this.contextLost) return
    if (t >= this.completeAt) {
      this.finish()
      return
    }
    this.raf = requestAnimationFrame(this.tick)
  }

  private finish(): void {
    if (this.finished || this.disposed) return
    this.finished = true
    this.cover.rotation.y = 2.18
    this.placeCamera(1)
    this.renderer.render(this.scene, this.camera)
    this.callbacks.onComplete?.()
    this.resolvePlay?.()
    this.resolvePlay = null
  }
}
